/*
 * iLive Chapters — Phase 6.14 Locked-Chapter Capture Modal
 *
 * Exposes: window.iLiveOpenChapterModal(slug, name)
 *   slug: era handle ("flame-within", "soul-forged", "dawn-reborn", "beyond")
 *   name: display string ("Flame Within", etc.)
 *
 * Called by era-carousel.js when a locked + centered era card is clicked.
 * Idempotent: second open call while modal is already open is a no-op.
 *
 * Submission strategy: NATIVE form post (the {% form 'customer' %} tag is
 * left intact in the snippet). Shopify handles hCaptcha server-side and
 * redirects to /?customer_posted=true on success. We stash the era's slug
 * + name to sessionStorage immediately before submit so that after the
 * page reload we can re-open the modal directly into success state.
 *
 * Why not AJAX intercept: Shopify's /contact endpoint requires hCaptcha for
 * customer-form POSTs and rejects token-less requests with 400 even though
 * the surrounding plumbing (form_type, fields) is correct. There's no way
 * to get a captcha token from the AJAX path without standing up a real
 * Shopify App. Native submit is fully supported and bulletproof.
 *
 * Phase 6.14 Task 5 of 5: open/close + focus trap + native-submit success
 * pickup. All modal behavior complete.
 */
(function () {
  'use strict';

  // Idempotency guard: only register once even if theme-editor reloads.
  if (window.iLiveChapterModalReady) return;

  var CONFIG = {
    AUTO_CLOSE_MS: 3000,
    TAG_SUFFIX: '-waitlist',
    UNIVERSAL_TAG: 'notify-waitlist',
    // sessionStorage key. Reset on close so a refresh doesn't re-pop the
    // success modal a second time.
    STORAGE_KEY: 'iLiveChapterModalPendingSuccess',
    // URL param Shopify appends on successful customer-form submit.
    SUCCESS_PARAM: 'customer_posted'
  };

  var modal = null;
  var dialog = null;
  var form = null;
  var emailInput = null;
  var nameSpans = null;
  var tagsInput = null;
  var noteInput = null;
  var submitBtn = null;
  var submitLabel = null;
  var errorEl = null;
  var formState = null;
  var successState = null;
  var lastFocus = null;
  var focusables = [];
  var autoCloseTimer = null;

  function init() {
    modal = document.getElementById('chapter-capture-modal');
    if (!modal) return;

    dialog = modal.querySelector('.chapter-modal__dialog');
    form = modal.querySelector('form');
    emailInput = modal.querySelector('[data-modal-email]');
    nameSpans = modal.querySelectorAll('[data-era-name]');
    tagsInput = modal.querySelector('[data-era-tags]');
    noteInput = modal.querySelector('[data-era-note]');
    submitBtn = modal.querySelector('[data-modal-submit]');
    submitLabel = modal.querySelector('[data-submit-label]');
    errorEl = modal.querySelector('[data-modal-error]');
    formState = modal.querySelector('[data-state="form"]');
    successState = modal.querySelector('[data-state="success"]');

    // On submit: stash the era info so the post-reload handler can find
    // it. Lock the button so accidental double-clicks don't fire two posts.
    // We do NOT preventDefault — let the form submit natively. Shopify
    // handles hCaptcha + redirect.
    if (form) {
      form.addEventListener('submit', handleSubmit);
    }

    // Close handlers: backdrop, close button, ESC
    modal.querySelectorAll('[data-modal-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) {
        close();
      }
    });

    // Focus trap
    modal.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || !modal.classList.contains('is-open')) return;
      if (focusables.length === 0) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    window.iLiveOpenChapterModal = open;
    window.iLiveChapterModalReady = true;

    // Post-reload success pickup. Runs once on every page load — if the
    // URL has ?customer_posted=true AND we have a pending era stashed,
    // pop the modal into success state.
    checkPostReloadSuccess();
  }

  function open(slug, name) {
    if (!modal) return;
    // Idempotency: bail if already open.
    if (modal.getAttribute('data-modal-open') === 'true') return;
    if (!slug || !name) return;

    // Populate per-era data via textContent (XSS-safe).
    nameSpans.forEach(function (span) { span.textContent = name; });
    tagsInput.value = slug + CONFIG.TAG_SUFFIX + ',' + CONFIG.UNIVERSAL_TAG;
    noteInput.value = 'Locked chapter signup: ' + name;

    lastFocus = document.activeElement;

    modal.setAttribute('data-modal-open', 'true');
    modal.setAttribute('aria-hidden', 'false');
    modal.classList.add('is-open');

    focusables = Array.prototype.slice.call(
      dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), [tabindex]:not([tabindex="-1"])'
      )
    );

    requestAnimationFrame(function () {
      if (emailInput) emailInput.focus();
    });
  }

  function close() {
    if (!modal) return;

    // Cancel any pending auto-close timer.
    if (autoCloseTimer) {
      clearTimeout(autoCloseTimer);
      autoCloseTimer = null;
    }

    if (modal.getAttribute('data-modal-open') !== 'true') return;

    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    modal.removeAttribute('data-modal-open');

    if (lastFocus && typeof lastFocus.focus === 'function') {
      lastFocus.focus();
    }
    lastFocus = null;

    // Reset form state for next open.
    if (form) form.reset();
    if (errorEl) hideError();
    if (submitBtn) setLoading(false);

    // Reset visible state back to form view for next open.
    if (formState && successState) {
      formState.hidden = false;
      successState.hidden = true;
    }
  }

  function handleSubmit(e) {
    // Client-side validation only. Do NOT preventDefault on a valid form —
    // we want the native post.
    if (!emailInput || !emailInput.checkValidity()) {
      e.preventDefault();
      showError('Please enter a valid email address.');
      return;
    }
    hideError();
    setLoading(true);

    // Stash era info so the post-reload handler can pop the success state
    // with the correct era name. Two fields:
    //   slug — used to verify the stash matches the current signup attempt
    //   name — the display string to show in the success message
    try {
      var name = '';
      if (nameSpans && nameSpans.length > 0) {
        name = nameSpans[0].textContent || '';
      }
      var tags = tagsInput ? tagsInput.value : '';
      var slug = tags.split(CONFIG.TAG_SUFFIX)[0]; // first chunk before -waitlist
      sessionStorage.setItem(CONFIG.STORAGE_KEY, JSON.stringify({
        slug: slug,
        name: name,
        ts: Date.now()
      }));
    } catch (err) {
      // sessionStorage may be unavailable (private mode in some browsers,
      // disabled in user settings). Submit still proceeds; user just won't
      // get the post-reload success popup.
      console.warn('[chapter-modal] sessionStorage write failed', err);
    }

    // No preventDefault — let the form post natively. Shopify will redirect
    // to /?customer_posted=true on success.
  }

  function checkPostReloadSuccess() {
    // Quick URL check — if no success param, nothing to do.
    var hasSuccessParam = (location.search.indexOf(CONFIG.SUCCESS_PARAM + '=true') !== -1);
    if (!hasSuccessParam) return;

    // Pull the stashed era info.
    var stashed = null;
    try {
      var raw = sessionStorage.getItem(CONFIG.STORAGE_KEY);
      if (raw) stashed = JSON.parse(raw);
    } catch (err) {
      console.warn('[chapter-modal] sessionStorage read failed', err);
    }

    // Always clear the stash so the next page navigation doesn't re-pop
    // a stale success modal.
    try { sessionStorage.removeItem(CONFIG.STORAGE_KEY); } catch (_) {}

    // Decide what era name to show. Priority:
    //   1. The stashed name (our modal's submit, survived sessionStorage)
    //   2. Generic fallback "you" so the modal still shows confirmation
    //      even if sessionStorage was wiped by the captcha challenge flow
    //      (this also fires for footer newsletter signups — accepted
    //      tradeoff: user gets a friendly thanks rather than nothing).
    var eraName = (stashed && stashed.name) ? stashed.name : null;

    // If we have NO stash AND the customer_posted param is here, this could
    // be a footer newsletter signup (not ours). Bail to avoid showing our
    // modal for non-modal submits. The footer renders its own success
    // message via its {% if form.posted_successfully? %} block.
    if (!eraName) return;

    // Our signup only (footer signups bailed above, so they keep their
    // #FooterNewsletter jump to the thank-you). Strip the ?customer_posted
    // param and the form's hash so the user lands at the top of the page
    // where the modal will appear.
    if (window.history && window.history.replaceState) {
      try {
        window.history.replaceState({}, '', location.pathname);
      } catch (_) {}
    }
    // Belt-and-suspenders: scroll to top in case the browser already
    // jumped to the hash before replaceState ran.
    window.scrollTo(0, 0);

    // Open the modal, populate the era name, swap to success state.
    nameSpans.forEach(function (span) { span.textContent = eraName; });

    // Don't use the normal open() path because that focuses the email
    // input — for success state, we don't want to land focus on a hidden
    // input. Set up the modal manually.
    lastFocus = document.activeElement;
    modal.setAttribute('data-modal-open', 'true');
    modal.setAttribute('aria-hidden', 'false');
    modal.classList.add('is-open');

    if (formState && successState) {
      formState.hidden = true;
      successState.hidden = false;
    }

    // Move focus to the dialog so screen readers announce the success.
    // The dialog itself is non-focusable by default; we add tabindex=-1
    // temporarily.
    if (dialog) {
      dialog.setAttribute('tabindex', '-1');
      requestAnimationFrame(function () {
        dialog.focus();
      });
    }

    // Auto-close after configured delay.
    autoCloseTimer = setTimeout(close, CONFIG.AUTO_CLOSE_MS);
  }

  function setLoading(loading) {
    if (!submitBtn || !submitLabel) return;
    submitBtn.disabled = loading;
    submitLabel.textContent = loading ? 'Sending…' : 'Notify Me';
  }

  function showError(msg) {
    if (!errorEl) return;
    errorEl.textContent = msg;
    errorEl.hidden = false;
  }

  function hideError() {
    if (!errorEl) return;
    errorEl.textContent = '';
    errorEl.hidden = true;
  }

  // Init on DOMContentLoaded (script is defer'd, so DOM is ready, but be safe).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
